import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import SeoHead from "../../seo/SeoHead.jsx";

/**
 * Replaces the previous `<Route path="*" element={<Navigate to="/" replace />} />`.
 *
 * That redirect returned HTTP 200 with the homepage for every bad URL, which
 * Google indexes as a soft 404 - a pile of duplicate homepages under junk URLs.
 * This page renders `noindex` instead, and react-snap writes it to build/404.html
 * so nginx can serve it with a genuine 404 status (see deploy/nginx.conf.example).
 */
const NotFound = () => (
  <div className="max-w-[720px] mx-auto px-[22px] py-[70px] pb-[90px]">
    {/* Title must contain "404" — react-snap detects the 404 page by that
        string and warns at build time when it's missing. */}
    <SeoHead
      title="404 — Page Not Found | EasyFile2290"
      description="The page you were looking for doesn't exist."
      path="/404"
      noindex
    />

    <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
      Error 404
    </div>
    <h1 className="text-[34px] leading-[1.1] mt-1.5 font-disp">This page doesn't exist</h1>
    <p className="text-[var(--muted)] mt-2.5 max-w-[58ch]">
      The link may be out of date, or the address may have a typo. Here's where most people are heading:
    </p>

    <div className="grid gap-3 mt-8">
      {[
        ["/", "Home", "File Form 2290 and get your stamped Schedule 1"],
        ["/blog", "Form 2290 guides", "Deadlines, amendments, credits, and Schedule 1"],
        ["/rates", "Rates & weight categories", "The full TY2026 rate table"],
        ["/faq", "FAQ", "Answers from the IRS instructions"],
      ].map(([to, label, body]) => (
        <Panel key={to} padded className="hover:border-[var(--accent)] transition-colors">
          <Link to={to} className="no-underline text-inherit">
            <b className="font-disp text-[16px]">{label}</b>
            <p className="text-[var(--muted)] text-sm mt-1">{body}</p>
          </Link>
        </Panel>
      ))}
    </div>

    <Link to="/wizard/1" className="inline-block mt-8">
      <Button>
        Start filing <ArrowRight size={16} />
      </Button>
    </Link>
  </div>
);

export default NotFound;
